/**
 * EVO — global qidiruv: API mijozi, marshrutlash va «oxirgi ochilganlar».
 *
 * Asos: TZ v8.0 §8 (Smart Search / Command), TZ v4.0 §7 (Global Search).
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN MARSHRUTLASH SHU YERDA, KOMPONENTDA EMAS                      │
 * │                                                                          │
 * │ «Tur → qaysi ekran» bilimi buyruq panelining ko'rinishiga umuman bog'liq │
 * │ emas: ertaga bosh sahifadagi vidjet yoki bildirishnoma ham o'sha         │
 * │ havolani qurishi kerak bo'ladi. Komponent ichida qolsa, ikkinchi         │
 * │ chaqiruvchi uni NUSXALARDI va ikkita jadval vaqt o'tib ajralib ketardi.  │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
import type { useNavigate } from '@tanstack/react-router';
import { api } from './api';
import type { TranslationKey } from './i18n';

// ---------------------------------------------------------------------------
// Server shartnomasi (`apps/api/src/modules/search/search.types.ts`)
// ---------------------------------------------------------------------------

export const SEARCH_TYPES = [
  'product',
  'customer',
  'supplier',
  'salesOrder',
  'purchaseOrder',
  'shipment',
  'invoice',
  'payment',
  'return',
  'installer',
  'delivery',
  'creditNote',
  'expense',
  'goodsReceipt',
  'employee',
] as const;

export type SearchType = (typeof SEARCH_TYPES)[number];

export interface SearchHit {
  readonly type: SearchType;
  readonly id: number;
  readonly code: string;
  readonly title: string;
  readonly meta: string | null;
  /** Arxivlangan/bloklangan/bekor qilingan yozuv — UI belgilaydi. */
  readonly inactive: boolean;
}

export interface SearchGroup {
  readonly type: SearchType;
  readonly items: readonly SearchHit[];
}

export interface SearchResponse {
  readonly q: string;
  readonly intent: 'docNo' | 'barcode' | 'phone' | 'text';
  readonly groups: readonly SearchGroup[];
  readonly total: number;
  /** Server tomonda o'lchangan davomiylik, ms. */
  readonly tookMs: number;
}

/**
 * Minimal uzunlik — SERVERDAGI bilan bir xil (`search.dto.ts`).
 *
 * Brauzerda takrorlanishi ATAYLAB: bitta harfda so'rov umuman YUBORILMASIN.
 * Serverdagi tekshiruv esa qoladi — u haqiqiy to'siq, bu faqat tejamkorlik.
 */
export const MIN_SEARCH_LENGTH = 2;

/** Panel yozilgandan keyin necha ms kutadi. */
export const SEARCH_DEBOUNCE_MS = 300;

export function globalSearch(q: string, signal: AbortSignal): Promise<SearchResponse> {
  return api.get<SearchResponse>('/search', { query: { q }, signal });
}

// ---------------------------------------------------------------------------
// Tur → sarlavha kaliti va marshrut
// ---------------------------------------------------------------------------

/** Guruh sarlavhasi. Matn `lib/i18n.ts` da — bu yerda faqat KALIT. */
export const SEARCH_TYPE_LABELS: Readonly<Record<SearchType, TranslationKey>> = {
  product: 'search.type.product',
  customer: 'search.type.customer',
  supplier: 'search.type.supplier',
  salesOrder: 'search.type.salesOrder',
  purchaseOrder: 'search.type.purchaseOrder',
  shipment: 'search.type.shipment',
  invoice: 'search.type.invoice',
  payment: 'search.type.payment',
  return: 'search.type.return',
  installer: 'search.type.installer',
  delivery: 'search.type.delivery',
  creditNote: 'search.type.creditNote',
  expense: 'search.type.expense',
  goodsReceipt: 'search.type.goodsReceipt',
  employee: 'search.type.employee',
};

type Navigate = ReturnType<typeof useNavigate>;

/**
 * Natijani ochadi.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ IKKI XIL EKRAN — VA BU FARQ ATAYLAB SAQLANGAN                            │
 * │                                                                          │
 * │ Ba'zi turlarda alohida kartochka marshruti bor (`/products/$id`), ba'zi- │
 * │ larida esa kartochka RO'YXAT ichida ochiladi (`/suppliers?id=…`,         │
 * │ `/invoices?id=…`). Bu qidiruvning qarori emas — `routes/index.tsx` da    │
 * │ shunday qilingan va sabab o'sha yerda yozilgan: ta'minotchilar va        │
 * │ moliya hujjatlari SOLISHTIRILADI, ya'ni ro'yxatni yo'qotmaslik kerak.    │
 * │                                                                          │
 * │ Qidiruv o'sha qarorga BO'YSUNADI, uni qaytadan hal qilmaydi: aks holda   │
 * │ bitta hujjat ikki xil manzilda ochilib, «orqaga» tugmasi kutilmagan      │
 * │ joyga olib borardi.                                                     │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ UCHINCHI HOLAT: EKRAN BOR, LEKIN YOZUVNI TANLAYDIGAN MANZIL YO'Q         ║
 * ║                                                                          ║
 * ║ Kredit-nota, xarajat, tovar qabuli va xodim ekranlarida ochilgan         ║
 * ║ kartochka `useState` da yashardi, URL da EMAS — ya'ni «shu hujjatni      ║
 * ║ och» degan manzil umuman mavjud emas edi va qidiruv foydalanuvchini      ║
 * ║ RO'YXATGA tashlab ketardi.                                               ║
 * ║                                                                          ║
 * ║ Uchtasi YOPILDI: kredit-nota, tovar qabuli va xodim endi `?id=` bilan    ║
 * ║ ochiladi (`lib/finance-search.ts`, `lib/procurement-search.ts`,          ║
 * ║ `lib/employees.ts` + `routes/index.tsx` dagi `validateSearch`).          ║
 * ║                                                                          ║
 * ║ XARAJAT hali qolgan: `routes/finance/expenses.tsx` kartochkani hamon     ║
 * ║ lokal holatda saqlaydi. Unga `?id=` yuborish hech narsa qilmasdi,        ║
 * ║ lekin manzilni «ishlayotgandek» ko'rsatardi — shuning uchun u ataylab    ║
 * ║ ro'yxatga olib boradi. Ekranda hujjat birinchi sahifada turadi           ║
 * ║ (ro'yxat «eng yangi avval»), ya'ni qolgani bitta ko'z yugurtish.         ║
 * ║                                                                          ║
 * ║ Yetkazish esa BOSHQA yo'ldan ishlaydi: `/sales/deliveries` da `q`        ║
 * ║ parametri bor va server uni yuk xati RAQAMI bo'yicha filtrlaydi          ║
 * ║ (`deliveries.service.ts`), ya'ni ro'yxat aynan bitta qatorga qisqaradi.  ║
 * ║ Tovar qabulida bunday `q` yo'q — shuning uchun u `?id=` ga o'tkazildi.   ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
export function openHit(navigate: Navigate, hit: SearchHit): void {
  const id = String(hit.id);

  switch (hit.type) {
    case 'product':
      void navigate({ to: '/products/$id', params: { id } });
      return;
    case 'customer':
      void navigate({ to: '/customers/$id', params: { id } });
      return;
    case 'salesOrder':
      void navigate({ to: '/sales/orders/$id', params: { id } });
      return;
    case 'purchaseOrder':
      void navigate({ to: '/procurement/orders/$id', params: { id } });
      return;
    case 'shipment':
      void navigate({ to: '/procurement/shipments/$id', params: { id } });
      return;
    case 'return':
      void navigate({ to: '/sales/returns/$id', params: { id } });
      return;
    case 'installer':
      void navigate({ to: '/loyalty/installers/$id', params: { id } });
      return;
    case 'supplier':
      void navigate({ to: '/suppliers', search: { id: hit.id } });
      return;
    case 'invoice':
      void navigate({ to: '/invoices', search: { id: hit.id } });
      return;
    case 'payment':
      void navigate({ to: '/payments', search: { id: hit.id } });
      return;
    /*
     * `q: hit.code` — yuk xati RAQAMI, `id` emas: ekran hujjatni raqam
     * bo'yicha topadi va ro'yxat bitta qatorga qisqaradi (yuqoridagi ramka).
     */
    case 'delivery':
      void navigate({ to: '/sales/deliveries', search: { q: hit.code } });
      return;
    /*
     * ╔══════════════════════════════════════════════════════════════════════╗
     * ║ BU UCHTASI ENDI YOZUVNI OCHADI, RO'YXATNI EMAS                       ║
     * ║                                                                      ║
     * ║ Avval ular ro'yxatga olib borardi va sabab shu yerda yozilgan edi:   ║
     * ║ o'sha ekranlarda ochilgan kartochka `useState` da yashardi, ya'ni    ║
     * ║ «shu hujjatni och» degan manzil MAVJUD EMAS edi. Foydalanuvchi       ║
     * ║ `CN-2026-00002` ni topib, Enter bosib, ro'yxatga tushardi va uni     ║
     * ║ qo'lda qayta izlardi — qidiruvning butun ma'nosi shu yerda           ║
     * ║ yo'qolardi.                                                          ║
     * ║                                                                      ║
     * ║ Endi kartochka `?id=` da (`lib/finance-search.ts`,                   ║
     * ║ `lib/procurement-search.ts`, `lib/employees.ts`) va havola qilinadi: ║
     * ║ manzilni nusxalab yuborsa ham, sahifani yangilasa ham o'sha hujjat   ║
     * ║ ochiladi.                                                            ║
     * ╚══════════════════════════════════════════════════════════════════════╝
     */
    case 'creditNote':
      void navigate({ to: '/finance/credit-notes', search: { id: hit.id } });
      return;
    /*
     * XARAJAT — HALI RO'YXAT.
     *
     * `routes/finance/expenses.tsx` hali `useState` da qolgan, ya'ni uning
     * kartochkasini ochadigan manzil yo'q. `?id=` ni shunchaki yuborish
     * ISHLAYOTGANDEK ko'rinadigan, lekin hech narsa qilmaydigan parametr
     * yasardi — bu fayl aynan shunday qilishni rad etadi.
     */
    case 'expense':
      void navigate({ to: '/finance/expenses' });
      return;
    case 'goodsReceipt':
      void navigate({ to: '/procurement/receipts', search: { id: hit.id } });
      return;
    case 'employee':
      void navigate({ to: '/hr/employees', search: { id: hit.id } });
      return;
  }
}

// ---------------------------------------------------------------------------
// Oxirgi ochilganlar
// ---------------------------------------------------------------------------

/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ NIMA UCHUN `localStorage`, SERVER EMAS                                    ║
 * ║                                                                          ║
 * ║ «Oxirgi ochilganlar» — panelning ENG KO'P ishlatiladigan holati: odam    ║
 * ║ kuni bo'yi 3-4 ta hujjat orasida u yoqdan bu yoqqa yuradi. U QIDIRUVSIZ  ║
 * ║ ishlashi kerak, ya'ni panel ochilishi bilan darhol ko'rinishi kerak.     ║
 * ║ Serverdan olinsa, har ochilishda kutish paydo bo'lardi — aynan eng tez   ║
 * ║ bo'lishi kerak bo'lgan yo'lda.                                           ║
 * ║                                                                          ║
 * ║ Maxfiylik: ro'yxatda faqat KOD, NOM va SANA saqlanadi — pul yo'q         ║
 * ║ (javobning o'zida ham yo'q). Bu foydalanuvchining O'ZI ko'rgan ma'lumot  ║
 * ║ va u o'z brauzerida qoladi.                                             ║
 * ║                                                                          ║
 * ║ CHEGARA: ro'yxat foydalanuvchiga bog'lanmagan. Bitta kompyuterda ikki    ║
 * ║ xodim navbatma-navbat ishlasa, ikkinchisi birinchisining hujjat          ║
 * ║ RAQAMLARINI ko'radi. Ochib bo'lmaydi — havola bosilganda server o'z      ║
 * ║ ruxsatini tekshiradi va 404 qaytaradi (`scope.ts` SCOPE_NOT_FOUND) —     ║
 * ║ lekin raqamning O'ZI ham ma'lumot. Shuning uchun «Chiqish» tugmasi       ║
 * ║ ro'yxatni TOZALAYDI (`topbar.tsx`).                                     ║
 * ║                                                                          ║
 * ║ ⚠️ YUQORIDAGI CHEGARA YOPILDI — ro'yxat endi FOYDALANUVCHIGA bog'langan. ║
 * ║                                                                          ║
 * ║ Kalitga `userId` qo'shildi (`evo.search.recents.42`). Ya'ni bitta        ║
 * ║ kompyuterda navbatma-navbat ishlaydigan ikki xodim bir-birining          ║
 * ║ ro'yxatini UMUMAN o'qimaydi — «Chiqish» bosilgan-bosilmaganidan          ║
 * ║ qat'iy nazar, va sessiya o'z-o'zidan tugagan holatda ham.                ║
 * ║                                                                          ║
 * ║ Muqobil («tozalashni `tokenStore.clear()` ga bog'lash») RAD ETILDI:      ║
 * ║ u faqat CHIQISH yo'lini yopardi va brauzer to'satdan yopilgan, quvvat    ║
 * ║ o'chgan yoki jarayon o'ldirilgan holatlarda ro'yxat joyida qolardi.      ║
 * ║ Kalitni ajratish esa hodisaga umuman bog'liq emas.                       ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
const RECENTS_PREFIX = 'evo.search.recents';

/** `evo.search.recents.42` — foydalanuvchining O'Z ro'yxati. */
function recentsKey(userId: number): string {
  return `${RECENTS_PREFIX}.${userId}`;
}

/** Shu brauzerdagi barcha foydalanuvchilarning kalitlari. */
function allRecentsKeys(): string[] {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key !== null && key.startsWith(RECENTS_PREFIX)) keys.push(key);
  }
  return keys;
}

/**
 * ESKI, EGASIZ ro'yxatni o'chiradi.
 *
 * Bu o'zgarishgacha ro'yxat `evo.search.recents` kalitida, foydalanuvchisiz
 * saqlanardi. U o'z-o'zidan yo'qolmaydi: yangi kod unga umuman qaramaydi va
 * eski yozuv brauzerda MUDDATSIZ qolib ketardi — ya'ni «kalitni ajratdik»
 * degan gap o'sha kompyuterlarda yolg'on bo'lardi.
 *
 * Shuning uchun har o'qishda bir marta tekshiriladi. Narxi — bitta
 * `getItem`, foydasi — ko'chish paytida ochiq qolgan ma'lumot yo'qoladi.
 */
function dropLegacyRecents(): void {
  if (localStorage.getItem(RECENTS_PREFIX) === null) return;
  localStorage.removeItem(RECENTS_PREFIX);
}

/**
 * Nechta saqlanadi.
 *
 * 5 — panelning bo'sh holatida bir qarashda o'qiladigan maksimal ro'yxat.
 * Undan ko'pi «tarix» ekraniga aylanadi va u boshqa vazifa: bu yerda kerak
 * bo'lgani — bir bosishda qaytish, izlash emas.
 */
const RECENTS_LIMIT = 5;

function isHit(value: unknown): value is SearchHit {
  if (typeof value !== 'object' || value === null) return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === 'number' &&
    typeof row.code === 'string' &&
    typeof row.title === 'string' &&
    typeof row.type === 'string' &&
    (SEARCH_TYPES as readonly string[]).includes(row.type)
  );
}

/**
 * O'qish HAR DOIM himoyalangan.
 *
 * `localStorage` — ISHONCHSIZ manba: uni foydalanuvchi qo'lda tahrirlashi
 * mumkin, eski versiya boshqa shaklda yozgan bo'lishi mumkin, private
 * rejimda esa u umuman ishlamaydi. Buzuq yozuv butun panelni yiqitmasligi
 * kerak — shuning uchun har qator alohida tekshiriladi va yaroqsizlari
 * jimgina tashlanadi.
 */
export function readRecents(userId: number): SearchHit[] {
  try {
    dropLegacyRecents();

    const raw = localStorage.getItem(recentsKey(userId));
    if (raw === null) return [];

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter(isHit)
      .slice(0, RECENTS_LIMIT)
      .map((hit) => ({
        type: hit.type,
        id: hit.id,
        code: hit.code,
        title: hit.title,
        meta: typeof hit.meta === 'string' ? hit.meta : null,
        inactive: hit.inactive === true,
      }));
  } catch {
    return [];
  }
}

/** Ochilgan yozuvni ro'yxat boshiga qo'yadi. Takror yozuv KO'CHIRILADI, qo'shilmaydi. */
export function pushRecent(userId: number, hit: SearchHit): SearchHit[] {
  const next = [
    hit,
    ...readRecents(userId).filter((row) => !(row.type === hit.type && row.id === hit.id)),
  ].slice(0, RECENTS_LIMIT);

  try {
    localStorage.setItem(recentsKey(userId), JSON.stringify(next));
  } catch {
    // Private rejim yoki to'lgan xotira — ro'yxat shu sessiyada yashaydi.
  }

  return next;
}

/**
 * Chiqishda tozalash.
 *
 * `userId` berilmasa — BARCHA foydalanuvchilarning ro'yxati o'chiriladi.
 * Bu sessiya o'z-o'zidan tugagan holat uchun: o'shanda kim bo'lgani
 * allaqachon noma'lum, lekin kompyuterda hech kimning ro'yxati qolmasligi
 * kerak. Chiqish tugmasi esa aniq `userId` beradi va faqat o'zinikini
 * o'chiradi — yonidagi hamkasbning ro'yxatiga tegmaydi.
 */
export function clearRecents(userId?: number): void {
  try {
    if (userId !== undefined) {
      localStorage.removeItem(recentsKey(userId));
      return;
    }

    for (const key of allRecentsKeys()) localStorage.removeItem(key);
  } catch {
    // Yuqoridagi bilan bir xil: tozalash imkoni bo'lmasa ham ekran ishlashda davom etadi.
  }
}
